import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientAddressEntity } from './client-address.entity';
import type { LicenseActivityEntity } from './license-activity.entity';
import { LicenseDisciplinaryActionEntity } from './license-disciplinary-action.entity';
import { LicenseFileEntity } from './license-file.entity';
import { LicenseNoteEntity } from './license-note.entity';
import { LicenseUserEntity } from './license-user.entity';

@Index('client_license_externalId_index', ['deletedUserId', 'externalId'], {})
@Index('fk_client_address__id', ['fkClientAddressId'], {})
@Index('idx_cl_status', ['status'], {})
@Index('idx_cl_registry_license', ['fkRegistryLicenseId'], {})
@Entity('client_license')
export class ClientLicenseEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 255 })
	public externalId!: string | null;

	/**
	 * Source classification — TITLE-CASE LABEL form. Canonical values are
	 * 'State Board' / 'NPI' / 'DEA' / 'Controlled Substance' / 'Tobacco' /
	 * 'Liquor' / 'KYC' / 'Other' (mirrors `LICENSE_SOURCE_TYPE_LABEL`).
	 *
	 * **Use `normalizeClientLicenseType()` for every write** — it produces
	 * the right title-case form. Do NOT use `normalizeLicenseSourceType()`
	 * here; that one returns the SCREAMING_SNAKE_CASE form used by
	 * `lm_registry.source_type` (a different column on a different table).
	 *
	 * Why title-case: the legacy SM1 system has a checkout-validation
	 * check `WHERE type = 'State Board'` (case-sensitive). A 2026-05-27
	 * migration that converted everything to `'STATE_BOARD'` broke that
	 * flow in production; the canonical form was reverted to the title-
	 * case label and a reconcile migration (`*-RevertClientLicenseType-
	 * StateBoardToLabel.ts`) shipped to fix the affected rows.
	 *
	 * Stored as varchar(255) so the entity stays non-destructive on
	 * existing DBs.
	 */
	@Column('varchar', { name: 'type', length: 255 })
	public type!: string;

	@Column('varchar', { name: 'licenseNumber', nullable: true, length: 255 })
	public licenseNumber!: string | null;

	@Index('idx_cl_license_number_normalized')
	@Column('varchar', {
		name: 'license_number_normalized',
		nullable: true,
		length: 100,
	})
	public licenseNumberNormalized!: string | null;

	@Column('date', { name: 'expirationDate', nullable: true })
	public expirationDate!: string | null;

	@Column('varchar', { name: 'status', length: 50, default: 'Active' })
	public status!: string;

	@Column('date', { name: 'issue_date', nullable: true })
	public issueDate!: string | null;

	@Column('varchar', { name: 'issuing_authority', length: 255, nullable: true })
	public issuingAuthority!: string | null;

	@Column('varchar', { name: 'issuing_state', length: 50, nullable: true })
	public issuingState!: string | null;

	@Column('tinyint', {
		name: 'has_disciplinary_action',
		width: 1,
		default: 0,
	})
	public hasDisciplinaryAction!: boolean;

	@Column('int', {
		name: 'fk_client_address__id',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressId!: number | null;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.clientLicenses,
		{ onDelete: 'NO ACTION', onUpdate: 'NO ACTION' },
	)
	@JoinColumn([{ name: 'fk_client_address__id', referencedColumnName: 'id' }])
	public fkClientAddress!: ClientAddressEntity;

	@Column('int', {
		name: 'fk_registry_license__id',
		nullable: true,
		unsigned: true,
	})
	public fkRegistryLicenseId!: number | null;

	@Column('datetime', { name: 'registry_verified_at', nullable: true })
	public registryVerifiedAt!: Date | null;

	@Column('varchar', {
		name: 'registry_status',
		nullable: true,
		length: 100,
	})
	public registryStatus!: string | null;

	@OneToMany('LicenseActivityEntity', 'fkLicense')
	public activities!: Array<LicenseActivityEntity>;

	@OneToMany(() => LicenseNoteEntity, (note) => note.fkLicense)
	public notes!: Array<LicenseNoteEntity>;

	@OneToMany(() => LicenseFileEntity, (file) => file.fkLicense)
	public licenseFiles!: Array<LicenseFileEntity>;

	@OneToMany(
		() => LicenseDisciplinaryActionEntity,
		(action) => action.fkLicense,
	)
	public disciplinaryActions!: Array<LicenseDisciplinaryActionEntity>;

	@OneToMany(() => LicenseUserEntity, (user) => user.fkLicense)
	public licenseUsers!: Array<LicenseUserEntity>;
}
